import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPageUrl } from '../utils/index.ts';
import { System } from '../api/entities.js';
import { SystemVersion } from '../api/entities.js';
import { CompanySystem } from '../api/entities.js';
import { Company } from '../api/entities.js';
import { User } from '../api/entities.js';
import { Button } from "../components/ui/button.jsx";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card.jsx";
import { Badge } from "../components/ui/badge.jsx";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs.jsx";
import { 
  ArrowLeft, 
  Edit, 
  Plus, 
  Package, 
  Tag, 
  Building2, 
  CalendarClock, 
  Clock, 
  ExternalLink,
  Trash2,
  AlertTriangle,
  CheckCircle2
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

export default function ViewSystem() {
  const navigate = useNavigate();
  const [system, setSystem] = useState(null);
  const [versions, setVersions] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Obter ID do sistema da URL
      const urlParams = new URLSearchParams(window.location.search);
      const systemId = urlParams.get('id');
      
      if (!systemId) {
        navigate(createPageUrl('Systems'));
        return;
      }

      // Verificar permissões do usuário
      const userData = await User.me();
      if (userData.role === 'client') {
        navigate(createPageUrl('Dashboard'));
        return;
      }

      // Carregar sistema
      const systemData = await System.findById(systemId);
      if (!systemData) {
        setError('Sistema não encontrado');
        setIsLoading(false);
        return;
      }
      setSystem(systemData);

      // Carregar versões do sistema
      const versionsData = await SystemVersion.list({ system_id: systemId });
      setVersions(versionsData || []);

      // Carregar empresas que utilizam o sistema
      const companiesWithSystem = await CompanySystem.list({ system_id: systemId });
      
      // Buscar detalhes completos de cada empresa
      if (companiesWithSystem && companiesWithSystem.length > 0) {
        const companyIds = companiesWithSystem.map(cs => cs.company_id);
        const companiesPromises = companyIds.map(id => {
          return Company.findById(id);
        });
        
        const companiesData = await Promise.all(companiesPromises);
        setCompanies(companiesData.filter(Boolean));
      }
    } catch (err) {
      console.error('Error loading system:', err);
      setError('Erro ao carregar dados do sistema');
    }
    
    setIsLoading(false);
  };

  // Obter a versão estável mais recente
  const getLatestStableVersion = () => {
    if (!versions || versions.length === 0) return null;
    
    const stableVersions = versions.filter(v => v.is_stable === true);
    if (stableVersions.length === 0) return null;
    
    // Ordenar versões do mais recente para o mais antigo
    stableVersions.sort((a, b) => {
      return new Date(b.release_date) - new Date(a.release_date);
    });
    
    return stableVersions[0];
  };

  // Renderizar status da versão como badge
  const renderVersionStatus = (isStable) => {
    return (
      <Badge className={isStable ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}>
        {isStable ? 'Estável' : 'Em Desenvolvimento'}
      </Badge>
    );
  };

  // Renderizar tipo do sistema como badge
  const renderSystemCategory = (type) => {
    const typeLabels = {
      'application': 'Aplicação',
      'database': 'Banco de Dados',
      'server': 'Servidor',
      'service': 'Serviço',
      'framework': 'Framework',
      'library': 'Biblioteca',
      'tool': 'Ferramenta',
      'other': 'Outro'
    };
    
    return (
      <Badge className="bg-blue-100 text-blue-800">
        {typeLabels[type] || type}
      </Badge>
    );
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" disabled>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="h-8 w-64 bg-gray-200 rounded animate-pulse" />
        </div>
        <div className="h-[300px] bg-gray-200 rounded animate-pulse" />
      </div>
    );
  }

  if (error || !system) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
        <h2 className="text-2xl font-bold">{error || 'Sistema não encontrado'}</h2>
        <p className="text-muted-foreground mt-2">
          Não foi possível carregar os dados do sistema solicitado.
        </p>
        <Button 
          onClick={() => navigate(createPageUrl('Systems'))}
          className="mt-6"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Voltar para Sistemas
        </Button>
      </div>
    );
  }

  const latestVersion = getLatestStableVersion();

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(createPageUrl('Systems'))}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold">{system.name}</h1>
            <div className="flex items-center gap-2 mt-1">
              {renderSystemCategory(system.type)}

              {latestVersion && (
                <span className="text-sm text-muted-foreground">
                  Versão atual: {latestVersion.version_number}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => navigate(createPageUrl(`NewSystemVersion?system_id=${system.id}`))}
          >
            <Tag className="mr-2 h-4 w-4" />
            Nova Versão
          </Button>
          {/* TODO: Implementar EditSystem.jsx */}
          {/* <Button 
            onClick={() => navigate(createPageUrl(`EditSystem?id=${system.id}`))}
            className="bg-primary"
          >
            <Edit className="mr-2 h-4 w-4" />
            Editar Sistema
          </Button> */}
        </div>
      </div>

      <Tabs defaultValue="details" className="w-full">
        <TabsList>
          <TabsTrigger value="details">Detalhes</TabsTrigger>
          <TabsTrigger value="versions">Versões ({versions.length})</TabsTrigger>
          <TabsTrigger value="companies">Empresas ({companies.length})</TabsTrigger>
        </TabsList>

        {/* Aba de Detalhes */}
        <TabsContent value="details" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Informações do Sistema</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {system.description && (
                <div>
                  <h3 className="text-sm font-medium mb-2">Descrição</h3>
                  <p className="text-muted-foreground">{system.description}</p>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-sm font-medium mb-2">SLA Padrão</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span>Prioridade Baixa:</span>
                      <span>{system.default_sla?.low || '-'} horas</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Prioridade Média:</span>
                      <span>{system.default_sla?.medium || '-'} horas</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Prioridade Alta:</span>
                      <span>{system.default_sla?.high || '-'} horas</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Prioridade Crítica:</span>
                      <span>{system.default_sla?.critical || '-'} horas</span>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-sm font-medium mb-2">Links</h3>
                  <div className="space-y-2">
                    {system.documentation_url ? (
                      <a 
                        href={system.documentation_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 text-primary hover:underline"
                      >
                        <ExternalLink className="h-4 w-4" />
                        Documentação
                      </a>
                    ) : (
                      <span className="text-muted-foreground">Nenhum link de documentação disponível</span>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Aba de Versões */}
        <TabsContent value="versions" className="space-y-4">
          {versions.length === 0 ? (
            <div className="text-center py-8 bg-white rounded-lg border">
              <Tag className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium">Nenhuma versão cadastrada</h3>
              <p className="text-muted-foreground mt-2 mb-6">
                Este sistema ainda não possui versões registradas.
              </p>
              <Button
                onClick={() => navigate(createPageUrl(`NewSystemVersion?system_id=${system.id}`))}
              >
                <Plus className="mr-2 h-4 w-4" />
                Adicionar Versão
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {versions
                .sort((a, b) => new Date(b.release_date) - new Date(a.release_date))
                .map(version => (
                  <Card 
                    key={version.id}
                    className="cursor-pointer hover:border-primary transition-colors"
                    onClick={() => navigate(createPageUrl(`ViewSystemVersion?id=${version.id}`))}
                  >
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 bg-primary/10 rounded-full flex items-center justify-center">
                            <Tag className="h-5 w-5 text-primary" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-lg">v{version.version_number}</h3>
                            <div className="flex items-center gap-2 mt-1">
                              {renderVersionStatus(version.is_stable)}
                              
                              {version.is_latest && (
                                <Badge className="bg-indigo-100 text-indigo-800">MAIS RECENTE</Badge>
                              )}
                              
                              {version.release_date && (
                                <span className="text-sm text-muted-foreground flex items-center gap-1">
                                  <CalendarClock className="h-3 w-3" />
                                  {format(new Date(version.release_date), 'dd/MM/yyyy')}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>

                      </div>
                      
                      {version.changelog && (
                        <div className="mt-4">
                          <p className="text-sm text-muted-foreground line-clamp-2">
                            {version.changelog}
                          </p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
            </div>
          )}
        </TabsContent>

        {/* Aba de Empresas */}
        <TabsContent value="companies" className="space-y-4">
          {companies.length === 0 ? (
            <div className="text-center py-8 bg-white rounded-lg border">
              <Building2 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium">Nenhuma empresa utiliza este sistema</h3>
              <p className="text-muted-foreground mt-2">
                Este sistema ainda não está associado a nenhuma empresa.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {companies.map(company => (
                <Card 
                  key={company.id}
                  className="cursor-pointer hover:border-primary transition-colors"
                  onClick={() => navigate(createPageUrl(`ViewCompany?id=${company.id}`))}
                >
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 bg-primary/10 rounded-full flex items-center justify-center">
                        <Building2 className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-semibold">{company.corporate_name}</h3>
                        {company.trade_name && (
                          <p className="text-sm text-muted-foreground">{company.trade_name}</p>
                        )}
                      </div>
                    </div>
                    
                    {/* Informações da instalação do sistema nesta empresa */}
                    <div className="mt-4 space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Versão:</span>
                        <span className="font-medium">-</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Status:</span>
                        <Badge className="bg-green-100 text-green-800">Ativo</Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}